/* The edit-profile flow's section registry. */

/* The four groups, in the live order. `step` is the ordinal the design shows as
   "STEP n OF 4"; it is the group's position, not the section's. */
export const EDIT_GROUPS = [
  { id: "About", title: "About", step: 1, heading: "Complete Your Business Profile" },
  { id: "Details", title: "Details", step: 2, heading: "Provide Business Details" },
  /* The Financial group is the one the design gives its own heading and
     intro; the others use the flow-wide pair EditStepHeader defaults to. */
  {
    id: "Financial",
    title: "Financial",
    step: 3,
    heading: "Business Financials Details",
    stepTitle: "Company Financials Details",
    stepIntro:
      "Banking, insurance, audited figures and the documents that back them up.",
  },
  {
    id: "Upload Document",
    /* The rail's label. The design's eyebrow reads "DOCUMENTS" while the rail
       still says "Upload Document", so the two are separate strings. */
    title: "Upload Document",
    eyebrow: "Documents",
    step: 4,
    heading: "Documents",
    stepTitle: "Documents",
    stepIntro:
      "Upload the official documents that support your profile. Supported formats: JPG, PNG, PDF.",
  },
];

export const TOTAL_STEPS = EDIT_GROUPS.length;

/* Every section, flattened, in the live order. `label` IS the live id.
   `navLabel` is the rail's shorter wording from the design, defaulting to the
   label where the design does not shorten it. */
export const EDIT_SECTIONS = [
  { group: "About", label: "About Business", navLabel: "About Company" },
  { group: "About", label: "Industry", navLabel: "Industry" },
  { group: "About", label: "Address & Contact Information", navLabel: "Address & Contact" },
  { group: "About", label: "Social Media", navLabel: "Social Media" },
  { group: "About", label: "Logo & Banner", navLabel: "Logo & Banner" },

  { group: "Details", label: "License Information", navLabel: "License Information" },
  { group: "Details", label: "Tax Information", navLabel: "Tax Information" },
  { group: "Details", label: "Ownership Structure", navLabel: "Ownership Structure" },
  { group: "Details", label: "Authorized Signatory", navLabel: "Authorized Signatory" },
  { group: "Details", label: "Key Personnel", navLabel: "Key Personnel" },
  { group: "Details", label: "Related Entities", navLabel: "Related Entities" },
  { group: "Details", label: "Business Relation", navLabel: "Business Relation" },

  { group: "Financial", label: "Banking Information", navLabel: "Banking Information" },
  { group: "Financial", label: "Insurance", navLabel: "Insurance" },
  { group: "Financial", label: "Auditor Information", navLabel: "Auditor Information" },
  { group: "Financial", label: "Financial Documents", navLabel: "Financial Documents" },

  /* ONE section, not three. MOA, Trade License and VAT Certificate render the VERY SAME card — UploadDocumentSection draws all three slots together — so three... */
  { group: "Upload Document", label: "MOA", navLabel: "Document" },
];

/* The section ids this flow used to have, and what they resolve to now.
   Without this a viewer whose saved position was "Trade License" would fall
   through resolveSection to the very first section of the flow. */
export const RETIRED_SECTION_IDS = {
  "Trade License": "MOA",
  "VAT Certificate": "MOA",
};

/* 17 — the real section count. */
export const TOTAL_SECTIONS = EDIT_SECTIONS.length;

export const groupSections = (groupId) =>
  EDIT_SECTIONS.filter((s) => s.group === groupId);

export const findSection = (label) =>
  EDIT_SECTIONS.find((s) => s.label === label) ||
  EDIT_SECTIONS.find((s) => s.label === RETIRED_SECTION_IDS[label]) ||
  null;

export const findGroup = (groupId) =>
  EDIT_GROUPS.find((g) => g.id === groupId) || null;

/* The index WITHIN its group — what the live `stepIndex` holds, and what every
   section's onNext/onPrev passes as its first argument. */
export const indexInGroup = (label) => {
  const section = findSection(label);
  if (!section) return -1;
  return groupSections(section.group).findIndex((s) => s.label === label);
};

/* Flow order across the whole flow, so Back and Continue can cross a group
   boundary the way the live buttons do (Logo & Banner → License Information). */
export const flowIndex = (label) =>
  EDIT_SECTIONS.findIndex((s) => s.label === label);

export const nextSection = (label) => {
  const i = flowIndex(label);
  return i === -1 ? null : EDIT_SECTIONS[i + 1] || null;
};

export const prevSection = (label) => {
  const i = flowIndex(label);
  return i <= 0 ? null : EDIT_SECTIONS[i - 1];
};

/* The design's eyebrow: "STEP 1 OF 4 · ABOUT". */
export const stepOf = (label) => {
  const section = findSection(label);
  const group = section && findGroup(section.group);
  return group ? group.step : 1;
};

/* The live page's fallback, transcribed: an unknown or missing saved label
   resolves to the first section of the first group rather than a blank screen
   (CompanyEditProfile.jsx:530-556). */
export const DEFAULT_SECTION = EDIT_SECTIONS[0].label;

/* Returns the section's CURRENT id, so a retired one ("Trade License") comes back as the section that replaced it rather than as itself — everything... */
export const resolveSection = (label) => findSection(label)?.label || DEFAULT_SECTION;
